import { parseDsn } from './dsn';

type Runtime = 'browser' | 'node' | 'edge';

type ReportContext = {
  runtime: Runtime;
  url?: string;
  method?: string;
  route?: string;
  release?: string;
  environment?: string;
  user?: { id?: string; email?: string };
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
};

const SDK = { name: 'mesh.custom', version: '0.1.0' };

function randomEventId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID().replace(/-/g, '');
  }
  return Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
}

function normalizeError(err: unknown): { type: string; value: string; stack?: string } {
  if (err instanceof Error) {
    const out: { type: string; value: string; stack?: string } = {
      type: err.name || 'Error',
      value: err.message || String(err),
    };
    if (err.stack) out.stack = err.stack;
    return out;
  }
  return { type: 'NonError', value: typeof err === 'string' ? err : JSON.stringify(err) };
}

// Release SHA — выставляется в env (`NEXT_PUBLIC_RELEASE=$(git rev-parse --short HEAD)`)
// перед `npm run build`. Browser-bundle подхватит из baked-in `NEXT_PUBLIC_*`,
// серверный код — runtime-env. Без него GlitchTip не сможет связать стектрейсы
// с конкретным деплоем.
const DEFAULT_RELEASE = process.env.NEXT_PUBLIC_RELEASE || undefined;

export async function reportError(err: unknown, context: ReportContext): Promise<void> {
  const dsn = parseDsn(
    context.runtime === 'browser'
      ? process.env.NEXT_PUBLIC_SENTRY_DSN
      : (process.env.SENTRY_DSN ?? process.env.NEXT_PUBLIC_SENTRY_DSN),
  );
  if (!dsn) return;

  const { type, value, stack } = normalizeError(err);
  const eventId = randomEventId();
  const timestamp = Date.now() / 1000;

  const event = {
    event_id: eventId,
    timestamp,
    platform: context.runtime === 'browser' ? 'javascript' : 'node',
    level: 'error',
    environment: context.environment ?? process.env.NODE_ENV ?? 'production',
    release: context.release ?? DEFAULT_RELEASE,
    sdk: SDK,
    server_name: context.runtime,
    tags: {
      runtime: context.runtime,
      ...(context.route ? { route: context.route } : {}),
      ...context.tags,
    },
    user: context.user,
    request: context.url ? { url: context.url, method: context.method } : undefined,
    exception: {
      values: [{ type, value, stacktrace: stack ? { frames: parseStack(stack) } : undefined }],
    },
    extra: context.extra,
  };

  const envelopeHeader = JSON.stringify({
    event_id: eventId,
    sent_at: new Date().toISOString(),
    dsn: dsn.origin,
    sdk: SDK,
  });
  const itemHeader = JSON.stringify({ type: 'event' });
  const body = `${envelopeHeader}\n${itemHeader}\n${JSON.stringify(event)}`;

  const auth = [
    'Sentry sentry_version=7',
    `sentry_client=${SDK.name}/${SDK.version}`,
    `sentry_key=${dsn.publicKey}`,
  ].join(', ');

  try {
    await fetch(dsn.envelopeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-sentry-envelope', 'X-Sentry-Auth': auth },
      body,
      keepalive: context.runtime === 'browser',
    });
  } catch {
    // Глотаем — мониторинг не должен ронять приложение.
  }
}

type StackFrame = {
  function?: string;
  filename?: string;
  lineno?: number;
  colno?: number;
};

function parseStack(stack: string): StackFrame[] {
  // Грубый парсер для V8/Firefox-стеков. Sentry-формат принимает frames в обратном порядке (oldest first).
  const lines = stack.split('\n').slice(1, 30);
  const frames = lines.map((line): StackFrame => {
    const v8 = line.match(/^\s*at\s+(?:(.+?)\s+\()?(.+?):(\d+):(\d+)\)?$/);
    if (v8) {
      const frame: StackFrame = {
        lineno: Number(v8[3]),
        colno: Number(v8[4]),
      };
      if (v8[1]) frame.function = v8[1];
      if (v8[2]) frame.filename = v8[2];
      return frame;
    }
    const ff = line.match(/^(.+?)@(.+?):(\d+):(\d+)$/);
    if (ff) {
      const frame: StackFrame = {
        lineno: Number(ff[3]),
        colno: Number(ff[4]),
      };
      if (ff[1]) frame.function = ff[1];
      if (ff[2]) frame.filename = ff[2];
      return frame;
    }
    return { function: line.trim() };
  });
  return frames.reverse();
}

export const hasMonitoring = Boolean(process.env.NEXT_PUBLIC_SENTRY_DSN ?? process.env.SENTRY_DSN);
